import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { UserEdit } from '../../components/employees/UserEdit';
import { getProfileRequirements } from 'api/user.api';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { ApiError, getApiErrorMessage, User } from './../../types';
import { useEffect, useState } from 'react';
import { doList as userDoList } from 'store/slices/userSlice';
import * as Sentry from '@sentry/react';
import { Button, Col, Result, Row, Skeleton } from 'antd';
import { SliceStatus } from 'constants/enums/slices';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';

const UserEditPage = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const { users, status: userStateStatus } = useAppSelector(
    (state) => state.user
  );
  const [employee, setEmployee] = useState<User | null>();
  const [seniorityDateRequired, setSeniorityDateRequired] = useState(false);
  const [profileRequirementsStatus, setProfileRequirementsStatus] = useState(
    SliceStatus.Empty
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (userStateStatus === SliceStatus.Empty) {
      dispatch(userDoList())
        .unwrap()
        .catch((error) => {
          const currentStatus = (error as ApiError).statusCode;
          notificationHook({
            type: 'error',
            message: statusMessages.user[
              currentStatus ? currentStatus : 500
            ] as string,
          });
        });
    } else if (userStateStatus === SliceStatus.Fulfilled && params.id) {
      const currentUser: User | undefined = users.find(
        (c) => c && c._id === params.id
      );
      if (currentUser) {
        setEmployee(currentUser);
      } else {
        setEmployee(null);
        notificationHook({
          type: 'error',
          message: statusMessages.user[404] as string,
        });
        Sentry.captureMessage('Not able to find a employee to edit');
      }
    }
  }, [params.id, userStateStatus, dispatch, users]);

  useEffect(() => {
    let isCurrentRequest = true;

    const loadProfileRequirements = async () => {
      if (!params.id) {
        setProfileRequirementsStatus(SliceStatus.Error);
        return;
      }

      setProfileRequirementsStatus(SliceStatus.Updating);
      try {
        const requirements = await getProfileRequirements(params.id);
        if (!isCurrentRequest) return;

        setSeniorityDateRequired(requirements.seniorityDateRequired);
        setProfileRequirementsStatus(SliceStatus.Fulfilled);
      } catch (error) {
        if (!isCurrentRequest) return;

        const currentStatus = (error as ApiError).statusCode;
        const fallbackMessage = statusMessages.user[
          currentStatus ?? 500
        ] as string;
        notificationHook({
          type: 'error',
          message: getApiErrorMessage(error, fallbackMessage),
        });
        setProfileRequirementsStatus(SliceStatus.Error);
      }
    };

    void loadProfileRequirements();
    return () => {
      isCurrentRequest = false;
    };
  }, [params.id]);

  const isLoading =
    userStateStatus === SliceStatus.Updating ||
    userStateStatus === SliceStatus.Empty ||
    profileRequirementsStatus === SliceStatus.Updating ||
    profileRequirementsStatus === SliceStatus.Empty;
  const hasLoadingError =
    userStateStatus === SliceStatus.Error ||
    profileRequirementsStatus === SliceStatus.Error ||
    (userStateStatus === SliceStatus.Fulfilled && employee === null);

  return (
    <>
      <PageTitle>{`Editar empleado`}</PageTitle>
      {isLoading && (
        <Row>
          <Col xs={24}>
            <Skeleton active />
          </Col>
        </Row>
      )}
      {hasLoadingError ? (
        <Result
          status="error"
          title="Hubo un error al cargar al empleado"
          subTitle="Por favor vuelve a intentarlo más tarde."
          extra={[
            <Button type="primary" key="back" onClick={() => navigate(-1)}>
              Regresar
            </Button>,
            <Button key="reload" onClick={() => window.location.reload()}>
              Reintentar
            </Button>,
          ]}
        />
      ) : (
        <>
          {userStateStatus === SliceStatus.Fulfilled &&
            profileRequirementsStatus === SliceStatus.Fulfilled &&
            employee && (
              <UserEdit
                user={employee}
                seniorityDateRequired={seniorityDateRequired}
              />
            )}
        </>
      )}
    </>
  );
};

export default UserEditPage;
